import React, { useState, useEffect } from 'react';
import { getBudgets, exportBudget, importBudget } from '../../../models/budgetsModel';
import BudgetsDashboard from './BudgetsDashboard';
import BudgetsTable from './BudgetsTable';
import BudgetForm from './BudgetForm';
import BudgetImport from './BudgetImport';
import BudgetYearImport from './BudgetYearImport';

function BudgetsList() {
  const [loading, setLoading] = useState(true);
  const [budgetsData, setBudgetsData] = useState({
    budget: {},
    summary: {}
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [showYearImportForm, setShowYearImportForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [createContext, setCreateContext] = useState(null);

  useEffect(() => {
    fetchBudgetData(selectedYear);
  }, [selectedYear]);

  const fetchBudgetData = async (year) => {
    try {
      setLoading(true);
      const response = await getBudgets(year);
      if (response.status === 1 || response.status === 'success') {
        const data = response.data || { budget: {}, summary: {} };
        setBudgetsData(data);
      }
    } catch (error) {
      console.error('Error fetching budgets data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await exportBudget({ year: selectedYear });
    } catch (error) {
      console.error('Error exporting budgets:', error);
    }
  };

  const handleImport = async (formData) => {
    try {
      await importBudget(formData);
      await fetchBudgetData(selectedYear); // Refresh data after import
      setShowImportForm(false);
    } catch (error) {
      console.error('Error importing budgets:', error);
      throw error; // Re-throw to let BudgetImport component handle it
    }
  };

  const handleCreateBudget = () => {
    setEditingBudget(null);
    setCreateContext(null);
    setShowCreateForm(true);
  };

  const handleCreateBudgetWithContext = (context) => {
    setEditingBudget(null);
    setCreateContext(context);
    setShowCreateForm(true);
  };

  const handleEditBudget = (budgetId) => {
    setEditingBudget(budgetId);
    setShowCreateForm(true);
  };

  const handleFormClose = () => {
    setShowCreateForm(false);
    setShowImportForm(false);
    setShowYearImportForm(false);
    setEditingBudget(null);
    setCreateContext(null);
    fetchBudgetData(selectedYear); // Refresh current year data
  };

  const getAvailableYears = () => {
    // Hardcoded years starting from 2024
    return ['2024', '2025', '2026', '2027', '2028', '2029', '2030', '2031', '2032', '2033', '2034', '2035'];
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Professional Header Card - Settings Pattern */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Budgets Management</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage organizational budgets and financial planning
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Year Selector */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Years</option>
                    {getAvailableYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={handleCreateBudget}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Budget
                </button>

                <button
                  onClick={() => setShowImportForm(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md transition-colors duration-200 border border-blue-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Import
                </button>

                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md transition-colors duration-200 border border-blue-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </button>
                <button
                  onClick={() => setShowYearImportForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Import Year
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'dashboard'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('table')}
                className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'table'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Budget Table
              </button>
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'dashboard' && (
              <BudgetsDashboard 
                budgetsData={budgetsData}
                selectedYear={selectedYear}
                onEditBudget={handleEditBudget}
              />
            )}

            {activeTab === 'table' && (
              <BudgetsTable 
                budgetsData={budgetsData}
                selectedYear={selectedYear}
                onEditBudget={handleEditBudget}
                onCreateBudget={handleCreateBudgetWithContext}
                onRefresh={() => fetchBudgetData(selectedYear)}
              />
            )}
          </div>
        </div>

      {/* Create/Edit Budget Modal */}
      {showCreateForm && (
        <BudgetForm
          budgetId={editingBudget}
          isOpen={showCreateForm}
          onClose={handleFormClose}
          initialValues={createContext}
        />
      )}

        {/* Import Budget Modal */}
        {showImportForm && (
          <BudgetImport
            isOpen={showImportForm}
            onClose={handleFormClose}
            onImport={handleImport}
          />
        )}

        {/* Import Year Budget Modal */}
        {showYearImportForm && (
          <BudgetYearImport
            isOpen={showYearImportForm}
            onClose={handleFormClose}
            onSuccess={() => fetchBudgetData(selectedYear)}
          />
        )}
      </div>
    </div>
  );
}

export default BudgetsList; 